import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Eye, Loader2, Plus, Search, X } from 'lucide-react';
import { emisoresApi } from '../../utils/api';
import useAuthStore from '../../store/authStore';
import toast from 'react-hot-toast';

export default function ListaEmpresas() {
  const navigate = useNavigate();
  const { setCurrentEmisor, setEmisores } = useAuthStore();
  const [emisores, setEmisoresList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const loadEmisores = async () => {
      try {
        const response = await emisoresApi.getMy();
        const list = response.data?.data || [];
        setEmisoresList(list);
        setEmisores(list);
      } catch (error) {
        console.error('Error loading emisores:', error);
      } finally {
        setLoading(false);
      }
    };
    loadEmisores();
  }, []);

  const handleVerEmpresa = (emisor) => {
    setCurrentEmisor(emisor);
    localStorage.setItem('emisorId', emisor.id);
    navigate(`/configuracion/empresa/${emisor.id}`);
  };

  const handleEmisorCreated = (newEmisor) => {
    setEmisoresList([...emisores, newEmisor]);
    setEmisores([...emisores, newEmisor]);
    setShowModal(false);
    toast.success('Empresa creada exitosamente');
  };

  const formatCedula = (tipo, identificacion) => {
    if (tipo === '02') {
      // Cédula jurídica: 3-102-706025
      const id = identificacion.toString();
      if (id.length === 10) {
        return `${id[0]}-${id.slice(1, 4)}-${id.slice(4)}`;
      }
    }
    return identificacion;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-4 flex justify-end">
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 border-2 border-blue-500 text-blue-600 px-4 py-2 rounded-lg hover:bg-blue-50 transition-colors"
        >
          <Plus className="h-5 w-5" />
          Agregar Empresa
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 tracking-wider">
                Nombre
              </th>
              <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 tracking-wider">
                Cédula Jurídica
              </th>
              <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 tracking-wider">
                Correo
              </th>
              <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 tracking-wider">
                Teléfono
              </th>
              <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 tracking-wider">
                Estado
              </th>
              <th className="text-center px-6 py-3 text-xs font-medium text-gray-500 tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {[...emisores].sort((a, b) => {
              const order = { '02': 0, '03': 1, '04': 2, '01': 3 };
              const oa = order[a.tipoIdentificacion || a.tipo_identificacion] ?? 2;
              const ob = order[b.tipoIdentificacion || b.tipo_identificacion] ?? 2;
              return oa - ob || (a.nombre || '').localeCompare(b.nombre || '');
            }).map((emisor) => (
              <tr key={emisor.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="h-14 w-14 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0"
                         style={{ backgroundColor: emisor.logo_url ? 'transparent' : '#f3f4f6' }}>
                      {emisor.logo_url ? (
                        <img src={emisor.logo_url} alt="" className="h-full w-full object-contain p-1" />
                      ) : (
                        <Building2 className="h-7 w-7 text-gray-400" />
                      )}
                    </div>
                    <span className="font-medium text-gray-900">{emisor.nombre}</span>
                  </div>
                </td>
                <td className="px-6 py-4 text-gray-600">
                  {formatCedula(emisor.tipo_identificacion || emisor.tipoIdentificacion, emisor.identificacion)}
                </td>
                <td className="px-6 py-4 text-gray-600">
                  {emisor.email || '-'}
                </td>
                <td className="px-6 py-4 text-gray-600">
                  {emisor.telefono_numero || '-'}
                </td>
                <td className="px-6 py-4">
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                    emisor.is_active !== false
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {emisor.is_active !== false ? 'Activa' : 'Inactiva'}
                  </span>
                </td>
                <td className="px-6 py-4 text-center">
                  <button
                    onClick={() => handleVerEmpresa(emisor)}
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
                  >
                    <Eye className="h-4 w-4" />
                    Ver
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {emisores.length === 0 && (
          <div className="p-12 text-center">
            <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No hay empresas configuradas</p>
            <button
              onClick={() => setShowModal(true)}
              className="mt-4 text-blue-600 hover:text-blue-700 font-medium"
            >
              Agregar primera empresa
            </button>
          </div>
        )}
      </div>

      {/* Modal Agregar Empresa */}
      {showModal && (
        <AgregarEmpresaModal
          onClose={() => setShowModal(false)}
          onSuccess={handleEmisorCreated}
        />
      )}
    </div>
  );
}

// Modal para agregar empresa
export function AgregarEmpresaModal({ onClose, onSuccess }) {
  const [cedula, setCedula] = useState('');
  const [loading, setLoading] = useState(false);
  const [consultando, setConsultando] = useState(false);
  const [formData, setFormData] = useState({
    tipoIdentificacion: '02', // Default: Jurídica
    identificacion: '',
    nombre: '',
    nombreComercial: '',
    email: '',
    telefono: '',
    actividadEconomica: ''
  });
  const [actividades, setActividades] = useState([]);

  const handleConsultarHacienda = async () => {
    if (!cedula || cedula.length < 9) {
      toast.error('Ingrese una cédula válida');
      return;
    }

    setConsultando(true);
    try {
      // Clean cedula (remove dashes)
      const cleanCedula = cedula.replace(/-/g, '');

      const response = await emisoresApi.consultarHacienda(cleanCedula);

      if (response.data?.success && response.data?.data) {
        const data = response.data.data;
        setFormData({
          tipoIdentificacion: data.tipoIdentificacion || '02',
          identificacion: data.identificacion || cleanCedula,
          nombre: data.nombre || '',
          nombreComercial: '',
          email: '',
          telefono: '',
          actividadEconomica: data.actividadPrincipal || ''
        });
        setActividades(data.actividades || []);
        toast.success('Información obtenida de Hacienda');
      } else {
        toast.error(response.data?.error || 'No se encontró información');
        // Still set the cedula
        setFormData(prev => ({
          ...prev,
          identificacion: cleanCedula
        }));
      }
    } catch (error) {
      console.error('Error consultando Hacienda:', error);
      toast.error('Error al consultar Hacienda');
      // Still set the cedula
      setFormData(prev => ({
        ...prev,
        identificacion: cedula.replace(/-/g, '')
      }));
    } finally {
      setConsultando(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.identificacion || !formData.nombre) {
      toast.error('Identificación y nombre son requeridos');
      return;
    }

    setLoading(true);
    try {
      const response = await emisoresApi.create(formData);

      if (response.data?.success) {
        onSuccess(response.data.data);
      } else {
        toast.error(response.data?.error || 'Error al crear empresa');
      }
    } catch (error) {
      console.error('Error creating emisor:', error);
      toast.error(error.response?.data?.error || 'Error al crear empresa');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Agregar Empresa</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Consultar por Cédula */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Consultar por Cédula
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={cedula}
                onChange={(e) => setCedula(e.target.value)}
                placeholder="Ej: 3102706025"
                className="flex-1 border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={handleConsultarHacienda}
                disabled={consultando}
                className="flex items-center gap-2 border-2 border-slate-500 text-slate-600 hover:bg-slate-50 px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
              >
                {consultando ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
                Consultar
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Ingrese la cédula para obtener los datos de Hacienda automáticamente
            </p>
          </div>

          <hr />

          {/* Tipo Identificación */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo de Identificación
            </label>
            <select
              value={formData.tipoIdentificacion}
              onChange={(e) => setFormData({ ...formData, tipoIdentificacion: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="01">Cédula Física</option>
              <option value="02">Cédula Jurídica</option>
              <option value="03">DIMEX</option>
              <option value="04">NITE</option>
            </select>
          </div>

          {/* Identificación */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Identificación *
            </label>
            <input
              type="text"
              value={formData.identificacion}
              onChange={(e) => setFormData({ ...formData, identificacion: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Nombre */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nombre / Razón Social *
            </label>
            <input
              type="text"
              value={formData.nombre}
              onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Nombre Comercial */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nombre Comercial
            </label>
            <input
              type="text"
              value={formData.nombreComercial}
              onChange={(e) => setFormData({ ...formData, nombreComercial: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Actividad Económica */}
          {actividades.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Actividad Económica
              </label>
              <select
                value={formData.actividadEconomica}
                onChange={(e) => setFormData({ ...formData, actividadEconomica: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Seleccione...</option>
                {actividades.map((act) => (
                  <option key={act.codigo} value={act.codigo}>
                    {act.codigo} - {act.descripcion}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Correo Electrónico
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Teléfono */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Teléfono
            </label>
            <input
              type="text"
              value={formData.telefono}
              onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border-2 border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 border-2 border-blue-500 text-blue-600 px-4 py-2 rounded-lg hover:bg-blue-50 disabled:opacity-50 transition-colors"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              Crear Empresa
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
